import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  MessageSquare, Send, Loader2, Search, Phone, User,
  ArrowLeft, ChevronRight, Plus, X, Bot, Pause, Play,
} from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h`;
  if (diff < 604800) return `${Math.round(diff / 86400)}d`;
  return d.toLocaleDateString();
}

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

const POLL_MS = 20_000;

export default function SmsInbox() {
  const [convos, setConvos] = useState([]);
  const [selected, setSelected] = useState(null);   // phone string
  const [thread, setThread] = useState({ messages: [], lead: null });
  const [busy, setBusy] = useState(true);
  const [threadBusy, setThreadBusy] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState("");
  // "New message" composer
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeQuery, setComposeQuery] = useState("");
  const [composeResults, setComposeResults] = useState([]);
  const [composePhone, setComposePhone] = useState("");
  const [composeLead, setComposeLead] = useState(null); // {id, name, phone}
  const [composeBody, setComposeBody] = useState("");
  const [composeSending, setComposeSending] = useState(false);
  const [composeSearching, setComposeSearching] = useState(false);
  const bottomRef = useRef(null);

  const loadConvos = useCallback(async () => {
    try {
      const { data } = await api.get("/sms/conversations");
      setConvos(data.conversations || []);
    } catch (e) {
      if (busy) toast.error(e?.response?.data?.detail || "Inbox load failed");
    } finally {
      setBusy(false);
    }
  }, [busy]);

  const loadThread = useCallback(async (phone) => {
    if (!phone) return;
    setThreadBusy(true);
    try {
      const { data } = await api.get("/sms/thread", { params: { phone } });
      setThread(data);
      // Refresh conversation list so unread count clears
      loadConvos();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Thread load failed");
    } finally {
      setThreadBusy(false);
    }
  }, [loadConvos]);

  useEffect(() => { loadConvos(); }, [loadConvos]);
  useEffect(() => {
    if (selected) loadThread(selected);
  }, [selected, loadThread]);

  // Deep-link support from AI Lead Manager (Text Seller button) or CRM:
  //   /sms-inbox?lead=<id>&phone=<e164>
  // If the phone already has a thread → auto-select it. Otherwise open
  // the compose panel with the seller's phone/name/lead prefilled so the
  // rep can send the first text without leaving this page.
  const [searchParams, setSearchParams] = useSearchParams();
  const dlLead   = searchParams.get("lead");
  const dlPhone  = searchParams.get("phone");
  useEffect(() => {
    if (!dlLead && !dlPhone) return;
    let alive = true;
    (async () => {
      try {
        let lead = null, phone = dlPhone || "";
        if (dlLead) {
          try {
            const { data } = await api.get(`/leads/${dlLead}`);
            if (data) { lead = data; phone = phone || data.phone || ""; }
          } catch { /* ignore */ }
        }
        if (!alive) return;
        // Prefer matching against the already-loaded conversations by
        // lead_id — SMS conversations are keyed on the phone SignalWire
        // received, which may not equal `leads.phone` byte-for-byte
        // (formatting, leading '+', etc.). If we find one, use its
        // phone so setSelected + loadThread hit the exact thread.
        const convPhone = (convos || []).find(
          (c) => c.lead_id === (lead?.id || dlLead) || (phone && c.phone === phone),
        )?.phone;
        const finalPhone = convPhone || phone;
        if (finalPhone) {
          setSelected(finalPhone);
        } else {
          setComposeOpen(true);
          setComposePhone(phone || "");
          setComposeLead(lead ? { id: lead.id, name: lead.name, phone: lead.phone } : null);
        }
      } finally {
        setSearchParams({}, { replace: true });
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dlLead, dlPhone, convos.length]);

  // Poll for new messages every 20s
  useEffect(() => {
    const t = setInterval(() => {
      loadConvos();
      if (selected) loadThread(selected);
    }, POLL_MS);
    return () => clearInterval(t);
  }, [selected, loadConvos, loadThread]);

  // Autoscroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [thread.messages.length]);

  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    setSending(true);
    try {
      await api.post("/twilio/sms/send", {
        to:      selected,
        body:    reply.trim(),
        lead_id: thread.lead?.id || null,
      });
      setReply("");
      await loadThread(selected);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const [aiBusy, setAiBusy] = useState(false);
  const ai = thread.ai_state || {};
  const aiLeadId = thread.lead?.id || ai.lead_id;
  const toggleAi = async () => {
    if (!aiLeadId) return;
    setAiBusy(true);
    try {
      const endpoint = ai.paused ? "resume" : "pause";
      await api.post(`/ai-sms/${endpoint}/${aiLeadId}`);
      toast.success(ai.paused
        ? "AI resumed — it'll continue the conversation"
        : "You've taken over — AI is paused on this thread");
      await loadThread(selected);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update AI");
    } finally { setAiBusy(false); }
  };

  const visibleConvos = filter.trim()
    ? convos.filter((c) =>
        (c.name || "").toLowerCase().includes(filter.toLowerCase()) ||
        (c.phone || "").includes(filter))
    : convos;

  // ============ New message composer ============
  // Debounced lead search — matches by name / phone / property_address
  useEffect(() => {
    if (!composeOpen) return;
    const q = composeQuery.trim();
    if (q.length < 2) {
      setComposeResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setComposeSearching(true);
      try {
        const { data } = await api.get("/leads", { params: { q, limit: 15 } });
        // Only leads with a phone are useful for SMS
        setComposeResults((data || []).filter((l) => l.phone).slice(0, 12));
      } catch (_) {
        setComposeResults([]);
      } finally {
        setComposeSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [composeQuery, composeOpen]);

  const openComposer = () => {
    setComposeOpen(true);
    setComposeQuery("");
    setComposeResults([]);
    setComposePhone("");
    setComposeLead(null);
    setComposeBody("");
  };
  const closeComposer = () => setComposeOpen(false);

  const pickLeadForCompose = (lead) => {
    setComposeLead(lead);
    setComposePhone(lead.phone);
    setComposeQuery(lead.name || lead.phone);
    setComposeResults([]);
  };

  const sendCompose = async () => {
    const phone = composePhone.trim();
    if (!/^\+?\d[\d\s\-()]{7,}$/.test(phone)) {
      toast.error("Enter a valid phone number");
      return;
    }
    if (!composeBody.trim()) {
      toast.error("Type a message");
      return;
    }
    setComposeSending(true);
    try {
      await api.post("/twilio/sms/send", {
        to:      phone,
        body:    composeBody.trim(),
        lead_id: composeLead?.id || null,
      });
      toast.success(`Sent to ${composeLead?.name || phone}`);
      setComposeOpen(false);
      // Refresh + open that thread
      await loadConvos();
      setSelected(phone.replace(/[^\d+]/g, "").replace(/^(\d{10})$/, "+1$1"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setComposeSending(false);
    }
  };

  return (
    <div className="flex h-[calc(100dvh-3.5rem)] md:h-[100dvh]" data-testid="sms-inbox-page">
      {/* LEFT — conversation list */}
      <aside className={`w-full sm:w-80 md:w-96 shrink-0 border-r border-[var(--app-line)] bg-white flex flex-col ${selected ? "hidden sm:flex" : "flex"}`}
             data-testid="sms-convos-list">
        <div className="px-4 pt-10 md:pt-12 pb-3 border-b border-[var(--app-line)]">
          <div className="flex items-center justify-between">
            <div>
              <span className="app-eyebrow">/ SMS</span>
              <h1 className="font-display text-2xl mt-1 flex items-center gap-2">
                <MessageSquare size={22} /> Inbox
              </h1>
            </div>
            <button
              onClick={openComposer}
              className="w-9 h-9 rounded-full bg-[var(--app-black)] text-white flex items-center justify-center hover:opacity-90 transition"
              title="New message"
              data-testid="sms-new-message-btn"
            >
              <Plus size={16} strokeWidth={2.5} />
            </button>
          </div>
          <div className="relative mt-3">
            <Search size={13} className="absolute left-2.5 top-2.5 text-muted-foreground" />
            <input
              type="search"
              placeholder="Search name or phone…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-[var(--app-line)] rounded-full focus:outline-none focus:ring-1 focus:ring-[var(--app-accent)]"
              data-testid="sms-search"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {busy && (
            <div className="p-8 text-center text-muted-foreground">
              <Loader2 className="animate-spin mx-auto mb-2" size={16} />
              Loading…
            </div>
          )}
          {!busy && visibleConvos.length === 0 && (
            <div className="p-8 text-center text-sm text-muted-foreground">
              <MessageSquare className="mx-auto mb-2 opacity-30" size={32} />
              No SMS conversations yet.
              <div className="text-xs mt-2 opacity-70">
                Inbound texts to your Twilio number will show up here.
              </div>
            </div>
          )}
          {!busy && visibleConvos.map((c) => {
            const isActive = selected === c.phone;
            return (
              <button
                key={c.phone}
                onClick={() => setSelected(c.phone)}
                data-testid={`sms-convo-${c.phone}`}
                className={`w-full text-left px-4 py-3 border-b border-[var(--app-line)] transition ${
                  isActive
                    ? "bg-[var(--app-accent)]/5 border-l-2 border-l-[var(--app-accent)]"
                    : "hover:bg-[var(--app-surface)]"
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <div className="font-medium text-sm truncate flex items-center gap-1.5">
                    {c.unread_count > 0 && (
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                    )}
                    {c.name || c.phone}
                  </div>
                  <span className="text-[10px] text-muted-foreground shrink-0">
                    {timeAgo(c.last_at)}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground truncate flex items-center gap-1">
                  {c.last_direction === "outbound" && <span className="opacity-50">You:</span>}
                  <span className={c.unread_count > 0 ? "text-[var(--app-black)] font-medium" : ""}>
                    {c.last_body || "—"}
                  </span>
                </div>
                {c.unread_count > 0 && (
                  <div className="mt-1 inline-block text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500 text-white font-semibold">
                    {c.unread_count} new
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </aside>

      {/* RIGHT — thread */}
      <div className={`flex-1 flex flex-col bg-[var(--app-surface)] ${selected ? "flex" : "hidden sm:flex"}`}>
        {!selected ? (
          <div className="flex-1 flex items-center justify-center text-center text-muted-foreground">
            <div>
              <MessageSquare size={44} className="opacity-20 mx-auto mb-3" />
              <div className="text-sm">Pick a conversation on the left</div>
            </div>
          </div>
        ) : (
          <>
            {/* Thread header */}
            <div className="px-4 pt-10 md:pt-12 pb-3 border-b border-[var(--app-line)] bg-white flex items-center gap-3"
                 data-testid="sms-thread-header">
              <button
                onClick={() => setSelected(null)}
                className="sm:hidden p-1 -ml-1 text-muted-foreground hover:text-[var(--app-black)]"
              >
                <ArrowLeft size={16} />
              </button>
              <div className="w-9 h-9 rounded-full bg-[var(--app-surface)] flex items-center justify-center shrink-0">
                {thread.lead ? <User size={14} /> : <Phone size={14} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">
                  {thread.lead?.name || selected}
                </div>
                <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                  <span className="font-mono">{selected}</span>
                  {thread.lead?.property_address && (
                    <>
                      <span>·</span>
                      <span className="truncate">{thread.lead.property_address}</span>
                    </>
                  )}
                </div>
              </div>
              {thread.lead && (
                <a
                  href={`/leads/${thread.lead.id}`}
                  className="text-xs text-[var(--app-accent-dark)] hover:underline flex items-center gap-0.5 shrink-0"
                  data-testid="sms-thread-open-lead"
                >
                  Open lead <ChevronRight size={11} />
                </a>
              )}
              {ai.exists && (
                ai.handed_off ? (
                  <span data-testid="sms-ai-state"
                        className="shrink-0 text-[11px] px-2 py-1 rounded-full bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200 flex items-center gap-1">
                    <Bot size={11} /> AI closed
                  </span>
                ) : (
                  <div className="flex items-center gap-2 shrink-0">
                    <span data-testid="sms-ai-state"
                          className={`text-[11px] px-2 py-1 rounded-full border flex items-center gap-1 ${
                            ai.paused ? "bg-amber-50 text-amber-700 border-amber-200"
                                      : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                      <Bot size={11} /> {ai.paused ? "Human mode" : "AI active"}
                    </span>
                    {aiLeadId && (
                      <button onClick={toggleAi} disabled={aiBusy}
                              data-testid="sms-ai-takeover-btn"
                              className={`text-[11px] px-2.5 py-1 rounded-full border font-medium transition-colors flex items-center gap-1 ${
                                ai.paused ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                          : "border-amber-300 text-amber-700 hover:bg-amber-50"}`}>
                        {aiBusy ? <Loader2 size={11} className="animate-spin" />
                                : ai.paused ? <Play size={11} /> : <Pause size={11} />}
                        {ai.paused ? "Resume AI" : "Take over"}
                      </button>
                    )}
                  </div>
                )
              )}
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2" data-testid="sms-thread-messages">
              {threadBusy && !thread.messages.length && (
                <Loader2 className="animate-spin mx-auto text-muted-foreground" size={16} />
              )}
              {thread.messages.map((m) => {
                const isOut = m.direction === "outbound";
                return (
                  <div key={m.id || m.message_sid}
                       className={`flex ${isOut ? "justify-end" : "justify-start"}`}
                       data-testid={`sms-msg-${m.direction}`}>
                    <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm ${
                      isOut
                        ? "bg-[var(--app-accent)] text-white rounded-br-sm"
                        : "bg-white border border-[var(--app-line)] text-[var(--app-black)] rounded-bl-sm"
                    }`}>
                      <div className="whitespace-pre-wrap break-words">{m.body}</div>
                      <div className={`text-[10px] mt-0.5 ${isOut ? "text-white/70" : "text-muted-foreground"}`}>
                        {fmtTime(m.created_at)}
                        {isOut && m.status && ` · ${m.status}`}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>

            {/* Compose */}
            <div className="border-t border-[var(--app-line)] bg-white p-3">
              <div className="flex gap-2 items-end">
                <textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey && !sending) {
                      e.preventDefault();
                      sendReply();
                    }
                  }}
                  placeholder="Type a message… (Enter to send, Shift+Enter for newline)"
                  rows={2}
                  className="flex-1 resize-none border border-[var(--app-line)] rounded-2xl px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[var(--app-accent)]"
                  data-testid="sms-reply-input"
                />
                <button
                  onClick={sendReply}
                  disabled={!reply.trim() || sending}
                  className="app-btn app-btn-primary h-10 shrink-0"
                  data-testid="sms-send-btn"
                >
                  {sending
                    ? <Loader2 size={13} className="animate-spin" />
                    : <><Send size={13} /> Send</>}
                </button>
              </div>
              <div className="text-[10px] text-muted-foreground mt-1.5 flex items-center justify-between">
                <span>{reply.length}/160 · SMS</span>
                <span>
                  Standard rates apply · STOP to opt-out is auto-handled
                </span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ============ New message composer modal ============ */}
      {composeOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
             data-testid="sms-compose-modal"
             onClick={(e) => { if (e.target === e.currentTarget) closeComposer(); }}>
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--app-line)]">
              <div className="flex items-center gap-2">
                <MessageSquare size={16} />
                <span className="font-display text-lg">New message</span>
              </div>
              <button onClick={closeComposer}
                      className="p-1 text-muted-foreground hover:text-[var(--app-black)]">
                <X size={16} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Recipient — search lead OR paste raw phone */}
              <div>
                <label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  To
                </label>
                <div className="relative mt-1">
                  <input
                    type="text"
                    autoFocus
                    value={composeQuery}
                    onChange={(e) => {
                      setComposeQuery(e.target.value);
                      // If the user is typing a raw phone number, mirror it into composePhone
                      const digits = e.target.value.replace(/\D/g, "");
                      if (digits.length >= 10 && !composeLead) {
                        setComposePhone(
                          digits.length === 11 && digits.startsWith("1")
                            ? `+${digits}`
                            : digits.length === 10 ? `+1${digits}` : `+${digits}`
                        );
                      }
                      if (composeLead && !e.target.value.includes(composeLead.name)) {
                        setComposeLead(null); // cleared the pill
                      }
                    }}
                    placeholder="Search a lead by name, or type a phone number…"
                    className="w-full px-3 py-2 text-sm border border-[var(--app-line)] rounded-xl focus:outline-none focus:ring-1 focus:ring-[var(--app-accent)]"
                    data-testid="sms-compose-search"
                  />
                  {composeSearching && (
                    <Loader2 size={13} className="absolute right-3 top-2.5 animate-spin text-muted-foreground" />
                  )}
                </div>

                {/* Selected lead pill */}
                {composeLead && (
                  <div className="mt-2 inline-flex items-center gap-2 px-2.5 py-1 bg-[var(--app-accent)]/10 rounded-full text-xs">
                    <User size={11} />
                    <span className="font-medium">{composeLead.name}</span>
                    <span className="font-mono text-muted-foreground">{composeLead.phone}</span>
                    <button onClick={() => {
                      setComposeLead(null);
                      setComposePhone("");
                      setComposeQuery("");
                    }}>
                      <X size={11} />
                    </button>
                  </div>
                )}

                {/* Autocomplete results */}
                {composeResults.length > 0 && !composeLead && (
                  <div className="mt-2 border border-[var(--app-line)] rounded-xl overflow-hidden divide-y divide-[var(--app-line)] max-h-56 overflow-y-auto">
                    {composeResults.map((l) => (
                      <button
                        key={l.id}
                        onClick={() => pickLeadForCompose(l)}
                        className="w-full text-left px-3 py-2 hover:bg-[var(--app-surface)] text-sm flex items-center justify-between gap-2"
                        data-testid={`sms-compose-result-${l.id}`}
                      >
                        <div className="min-w-0">
                          <div className="font-medium truncate">{l.name || "—"}</div>
                          <div className="text-[11px] text-muted-foreground truncate">
                            {l.property_address}
                          </div>
                        </div>
                        <div className="text-xs font-mono text-muted-foreground shrink-0">
                          {l.phone}
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {composePhone && !composeLead && (
                  <div className="mt-2 text-[11px] text-muted-foreground">
                    Will send to <span className="font-mono text-[var(--app-black)]">{composePhone}</span>
                  </div>
                )}
              </div>

              {/* Message body */}
              <div>
                <label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  Message
                </label>
                <textarea
                  value={composeBody}
                  onChange={(e) => setComposeBody(e.target.value)}
                  rows={4}
                  placeholder="Hey — quick question about your property at…"
                  className="w-full mt-1 px-3 py-2 text-sm border border-[var(--app-line)] rounded-xl resize-none focus:outline-none focus:ring-1 focus:ring-[var(--app-accent)]"
                  data-testid="sms-compose-body"
                />
                <div className="text-[10px] text-muted-foreground mt-1 flex items-center justify-between">
                  <span>{composeBody.length}/160 · SMS</span>
                  <span>STOP to opt-out is auto-handled</span>
                </div>
              </div>
            </div>

            <div className="px-5 py-3 border-t border-[var(--app-line)] bg-[var(--app-surface)] flex items-center justify-end gap-2">
              <button
                onClick={closeComposer}
                className="text-xs px-3 py-2 rounded-full border border-[var(--app-line)] hover:bg-white"
                data-testid="sms-compose-cancel"
              >
                Cancel
              </button>
              <button
                onClick={sendCompose}
                disabled={composeSending || !composePhone || !composeBody.trim()}
                className="app-btn app-btn-primary text-xs disabled:opacity-50"
                data-testid="sms-compose-send"
              >
                {composeSending
                  ? <Loader2 size={13} className="animate-spin" />
                  : <><Send size={13} /> Send SMS</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
